import { Injectable, inject } from "@angular/core";
import {
	Firestore,
	collection,
	doc,
	getDocs,
	getDoc,
	addDoc,
	updateDoc,
	deleteDoc,
	query,
	where,
	orderBy,
	onSnapshot,
} from "@angular/fire/firestore";
import { Observable, from, map, switchMap, throwError } from "rxjs";
import { httpsCallable, Functions } from "@angular/fire/functions";
import { Auth, getAuth } from "@angular/fire/auth";

import { Cohort, CohortStatus, MemberCohort } from "../models/cohort.model";

@Injectable({
	providedIn: "root",
})
export class CohortService {
	private firestore = inject(Firestore);
	private functions = inject(Functions);
	private auth = inject(Auth);

	// Helper method to get current user ID
	private getCurrentUserId(): string | null {
		const user = this.auth.currentUser;
		if (!user) {
			console.warn("No authenticated user found");
			return null;
		}
		return user.uid;
	}

	// Helper method to get current user ID with error handling
	private getCurrentUserIdOrThrow(): string {
		const userId = this.getCurrentUserId();
		if (!userId) {
			throw new Error("User not authenticated");
		}
		return userId;
	}

	// Get all cohorts
	getCohorts(): Observable<Cohort[]> {
		const cohortsRef = collection(this.firestore, "cohorts");
		return from(getDocs(cohortsRef)).pipe(
			map(
				(snapshot) =>
					snapshot.docs.map((doc) => ({
						id: doc.id,
						...doc.data(),
					})) as Cohort[],
			),
		);
	}

	// Get active cohorts for the current member
	getActiveMemberCohorts(): Observable<MemberCohort[]> {
		const userId = this.getCurrentUserIdOrThrow();

		// Query memberCohorts collection for active cohorts for this user
		const memberCohortsQuery = query(
			collection(this.firestore, "memberCohorts"),
			where("member_id", "==", userId),
			where("active", "==", true),
			orderBy("created_at", "desc"),
		);

		return from(getDocs(memberCohortsQuery)).pipe(
			map((snapshot) => {
				const memberCohorts = snapshot.docs.map((doc) => {
					const data = doc.data();
					return {
						id: doc.id,
						...data,
					};
				}) as MemberCohort[];
				
				// The memberCohorts collection already contains all necessary display data
				return memberCohorts;
			}),
		);
	}

	// Get a specific cohort by ID
	getCohort(id: string): Observable<Cohort | null> {
		const cohortRef = doc(this.firestore, `cohorts/${id}`);
		return from(getDoc(cohortRef)).pipe(
			map((doc) => {
				if (doc.exists()) {
					return { id: doc.id, ...doc.data() } as Cohort;
				}
				return null;
			}),
		);
	}

	// Create a new cohort
	createCohort(data: Partial<Cohort>): Observable<string> {
		const cohortsRef = collection(this.firestore, "cohorts");
		return from(addDoc(cohortsRef, data)).pipe(map((docRef) => docRef.id));
	}

	// Update a cohort
	updateCohort(id: string, data: Partial<Cohort>): Observable<void> {
		const cohortRef = doc(this.firestore, `cohorts/${id}`);
		return from(updateDoc(cohortRef, data));
	}

	// Delete a cohort
	deleteCohort(id: string): Observable<void> {
		const cohortRef = doc(this.firestore, `cohorts/${id}`);
		return from(deleteDoc(cohortRef));
	}

	// Get all cohorts for the current user
	getUserCohorts(): Observable<any[]> {
		const userId = this.getCurrentUserIdOrThrow();

		// Query organizationCohorts collection for documents where the user is a member
		const organizationCohortsQuery = query(
			collection(this.firestore, "organizationCohorts"),
			where("organization_id", "in", [userId]), // This would need to be adjusted based on your data structure
		);

		return from(getDocs(organizationCohortsQuery)).pipe(
			map((snapshot) =>
				snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				})),
			),
		);
	}

	// Get member cohorts
	getMemberCohorts(): Observable<any[]> {
		const userId = this.getCurrentUserIdOrThrow();

		// Get all organization cohorts
		return from(getDocs(collection(this.firestore, "organizationCohorts"))).pipe(
			switchMap((orgCohorts) => {
				if (orgCohorts.empty) {
					return from([]);
				}

				// For each organization cohort, check if the user is a member
				const memberPromises = orgCohorts.docs.map(async (orgCohortDoc) => {
					const orgCohortId = orgCohortDoc.id;
					const orgCohortData = orgCohortDoc.data();

					// Check if user is a member of this organization cohort
					const memberDoc = await getDoc(
						doc(this.firestore, `organizationCohorts/${orgCohortId}/members/${userId}`),
					);

					if (memberDoc.exists()) {
						return {
							id: orgCohortId,
							...orgCohortData,
							memberData: memberDoc.data(),
						};
					}

					return null;
				});

				return from(Promise.all(memberPromises)).pipe(
					map((results) => results.filter((result) => result !== null)),
				);
			}),
		);
	}

	// Get member cohort details
	getMemberCohort(organizationCohortId: string, memberId: string): Observable<any> {
		const memberRef = doc(
			this.firestore,
			`organizationCohorts/${organizationCohortId}/members/${memberId}`,
		);

		return from(getDoc(memberRef)).pipe(
			map((doc) => {
				if (doc.exists()) {
					return { id: doc.id, ...doc.data() };
				}
				return null;
			}),
		);
	}

	// Submit an answer for a cohort question
	submitAnswer(
		organizationCohortId: string,
		memberId: string,
		questionId: string,
		answer: any,
	): Observable<any> {
		const submitAnswerFunction = httpsCallable(this.functions, "submitAnswerToCohort");
		return from(
			submitAnswerFunction({
				organization_cohort_id: organizationCohortId,
				member_doc_id: memberId,
				questionId,
				answer,
			}),
		);
	}
}
